import _ from 'lodash';
import {
  INVERTED_AREA_UNITS,
  INVERTED_ROW_UNITS,
  INVERTED_YIELD_UNITS_BY_DEFAULT,
} from './units';
import { objectWithFields } from '../utils/utils';

export const CATEGORIES = [
  'installation',
  'management',
  'harvest',
  'marketing',
];

export const FILTER_CATEGORIES = [
  ...CATEGORIES,
  null, // don't filter categories
];

export const ASSIGNMENTS = { OWNER: 'owner', DEVELOPER: 'developer' };

export const OPERATION_UNITS = ['acre', 'tree', 'row_foot', 'lb', 'event'];

// A map from operation units to their corresponding unit specs.
// The 'lb' operation unit represents yield units of whatever
// the crop's yield unit is (which may be bft).
export const getOperationUnitSpecMap = (yieldUnitOption = 'lbs') => ({
  acre: INVERTED_AREA_UNITS,
  row_foot: INVERTED_ROW_UNITS,
  lb: INVERTED_YIELD_UNITS_BY_DEFAULT[yieldUnitOption],
});

export const getOperationUnitOptions = (unitsMap) =>
  OPERATION_UNITS.map((unit) => ({
    name: `per ${(unitsMap[unit]?.displayUnit || unit).replace('/', '')}`,
    // 'event' unit is stored as 'year' in the backend
    value: unit === 'event' ? 'year' : unit,
  }));

// Remove '/' from inverted displayUnits
export const getOperationCsvUnitsMap = (unitsMap) =>
  Object.entries(unitsMap).reduce(
    (map, [key, unit]) => ({
      ...map,
      [key]: {
        ...unit,
        displayUnit: unit.displayUnit.replace('/', ''),
        displayCsv: unit.displayCsv.replace('/', ''),
      },
    }),
    {
      year: {
        displayUnit: 'event',
        displayCsv: 'event',
        transformToUnit: _.identity,
        transformToDefault: _.identity,
      },
    },
  );

export const GROUPS = [
  'soil prep',
  'seeding',
  'planting',
  'mulching',
  'mowing',
  'pruning',
  'fertility',
  'irrigation',
  'spraying',
  'pest & disease',
  'harvesting',
  'timber harvesting',
  'processing',
  'marketing',
  'misc',
];

export const formatOperationGroup = (group) =>
  // Cannot use _.startCase because it removes the ampersand in 'pest & disease'
  group
    .split(' ')
    .map((word) => _.capitalize(word))
    .join(' ');

export const categoryOptions = CATEGORIES.map((category) => ({
  name: _.capitalize(category),
  value: category,
}));

export const groupOptions = GROUPS.map((item) => ({
  name: formatOperationGroup(item),
  value: item,
}));

export const fields = [
  'id',
  'name',
  'category',
  'group',
  'assignment',
  'millisecondsPerUnit',
  'unitForMilliseconds',
  'contracted',
  'rented',
  'dollarsPerUnit',
  'unitForDollars',
  'cadence',
  'startingYear',
  'endingYear',
  'timesPerYear',
  'years',
  'millisecondsOverrides',
  'equipmentRentalCostPerDay',
  'equipmentName',
  'numberOfLaborers',
  'valid',
];

export const bulkEditFields = [
  'category',
  'group',
  'assignment',
  'contracted',
  'rented',
  'dollarsPerUnit',
  'unitForDollars',
  'equipmentRentalCostPerDay',
  'millisecondsPerUnit',
  'unitForMilliseconds',
  'numberOfLaborers',
  'millisecondsOverrides',
  'lossEvents',
  'revenueStreams',
  'cadence',
  'startingYear',
  'endingYear',
  'timesPerYear',
  'years',
];

export const lossEventFields = ['id', 'name', 'percentTreeLoss'];

export const revenueStreamFields = ['id', 'name', 'percent'];

export const assignmentOptions = Object.values(ASSIGNMENTS).map(
  (assignment) => ({
    name: _.capitalize(assignment),
    value: assignment,
  }),
);

export const assignmentOptionsMap = _.keyBy(assignmentOptions, 'value');

export const ownerEquipmentTypes = {
  DIRECT_OPERATED: 'Operated by Owner',
  CONTRACTED: 'Contracted by Owner',
  RENTED: 'Equipment Rental by Owner',
};

export const developerEquipmentTypes = {
  DIRECT_OPERATED: 'Operated by Developer',
  CONTRACTED: 'Subcontracted by Developer',
  RENTED: 'Equipment Rental by Developer',
};

export const defaultOperation = {
  ...objectWithFields(fields),
  numberOfLaborers: 1,
  operationInputs: [],
  assignment: assignmentOptions[0].value,
  category: categoryOptions[0].value,
  group: groupOptions[0].value,
  timesPerYear: 1,
  unitForMilliseconds: OPERATION_UNITS[0],
  millisecondsOverrides: {},
  capexGroups: [],
  contracted: false,
  rented: false,
};
